import { type FC } from 'react'

import { getTailwindColor } from '@/utils/tailwindColors'
import { HighlightDotMarker } from './HighlightDotMarker'

const HIGHLIGHT_FILL = getTailwindColor('red', '600')

type GraphicalItemPoint = {
    x: number
    y: number
    value?: unknown
    payload?: Record<string, unknown>
}

/**
 * Shape of each entry in recharts' `formattedGraphicalItems` (injected by Customized).
 *
 * Important: `stroke` and `dataKey` live on the React element (`item.props`),
 * **not** on the computed `props` object. `Line.getComposedData` only returns
 * `{ points, layout, ...offset }` — visual styling props stay on the element.
 */
type FormattedGraphicalItem = {
    item: {
        type?: { displayName?: string }
        props?: { stroke?: string; dataKey?: string }
    }
    props: {
        points?: GraphicalItemPoint[]
    }
}

export type HighlightDotsOverlayProps = {
    highlightKeySet: Set<string>
    dataGrouping: 'aggregate' | 'compare'
    chartId: string
    /** Injected by recharts Customized component */
    formattedGraphicalItems?: FormattedGraphicalItem[]
}

const resolveLineColor = (rawColor: string | undefined): string => {
    return rawColor ?? HIGHLIGHT_FILL
}

/**
 * Renders highlight dots as a separate SVG layer on top of all chart lines.
 *
 * Designed to be used with recharts `<Customized component={...} />`.
 * The Customized wrapper injects `formattedGraphicalItems` which contains
 * every rendered graphical element with its computed pixel coordinates.
 */
export const HighlightDotsOverlay: FC<HighlightDotsOverlayProps> = (props) => {
    const { highlightKeySet, dataGrouping, chartId, formattedGraphicalItems } =
        props

    if (
        !formattedGraphicalItems ||
        formattedGraphicalItems.length === 0 ||
        highlightKeySet.size === 0
    ) {
        return null
    }

    const markers: React.JSX.Element[] = []

    for (const graphicalItem of formattedGraphicalItems) {
        const { props: itemProps, item } = graphicalItem

        // Only process Line components (skip Area, Bar, etc.)
        const displayName = item.type?.displayName
        if (displayName !== 'Line') {
            continue
        }

        const { points } = itemProps
        if (!points) {
            continue
        }

        // stroke & dataKey are component props on the React element, not on
        // the computed data returned by Line.getComposedData.
        const stroke = item.props?.stroke
        const dataKey = item.props?.dataKey

        const lineColor = resolveLineColor(stroke)

        for (const point of points) {
            const { x, y, payload } = point

            if (!payload || payload.__clone === true) {
                continue
            }

            const timestamp =
                payload.timestamp !== undefined
                    ? Number(payload.timestamp)
                    : undefined

            if (timestamp === undefined) {
                continue
            }

            const pointKey =
                dataGrouping === 'aggregate'
                    ? String(timestamp)
                    : `${String(timestamp)}|${dataKey ?? ''}`

            const isHighlight = highlightKeySet.has(pointKey)
            const isOnChart =
                typeof y === 'number' && y > 0 && Number.isFinite(y)

            if (!isHighlight || !isOnChart) {
                continue
            }

            const filterId = `${chartId}-highlight-${String(x)}-${String(y)}`

            markers.push(
                <HighlightDotMarker
                    key={filterId}
                    cx={x}
                    cy={y}
                    lineColor={lineColor}
                    filterId={filterId}
                />
            )
        }
    }

    if (markers.length === 0) {
        return null
    }

    return <g className="recharts-highlight-dots-overlay">{markers}</g>
}
